import { useLingui } from '@lingui/react/macro';

import AnnotatorConstants from '../modules/annotator/annotator.constants';

// Core values without translations
export const radiographicBoneLevelValues = {
  default: {
    value: 0,
    bitewings: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 3 },
      orange: { inclusiveLowerBound: 4 },
      red: { inclusiveLowerBound: 5 },
    },
    periapical: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 15 },
      orange: { inclusiveLowerBound: 24 },
      red: { inclusiveLowerBound: 33 },
    },
  },
  moderate: {
    value: 1,
    bitewings: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 2.5 },
      orange: { inclusiveLowerBound: 3.5 },
      red: { inclusiveLowerBound: 4.5 },
    },
    periapical: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 13 },
      orange: { inclusiveLowerBound: 22 },
      red: { inclusiveLowerBound: 31 },
    },
  },
  higher: {
    value: 2,
    bitewings: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 2 },
      orange: { inclusiveLowerBound: 3 },
      red: { inclusiveLowerBound: 4 },
    },
    periapical: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 10 },
      orange: { inclusiveLowerBound: 19 },
      red: { inclusiveLowerBound: 28 },
    },
  },
  maximum: {
    value: 3,
    bitewings: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 99 },
      orange: { inclusiveLowerBound: 99 },
      red: { inclusiveLowerBound: 3 },
    },
    periapical: {
      green: { inclusiveLowerBound: 0 },
      yellow: { inclusiveLowerBound: 99 },
      orange: { inclusiveLowerBound: 99 },
      red: { inclusiveLowerBound: 15 },
    },
  },
} as const;

// For backward compatibility - combines values with untranslated labels
export const radiographicBoneLevelSetting = {
  default: {
    label: 'Option A',
    value: radiographicBoneLevelValues.default.value,
    bitewings: {
      green: {
        label: 'Normal (<3mm)',
        ...radiographicBoneLevelValues.default.bitewings.green,
      },
      yellow: {
        label: '3-4 mm',
        ...radiographicBoneLevelValues.default.bitewings.yellow,
      },
      orange: {
        label: '4-5 mm',
        ...radiographicBoneLevelValues.default.bitewings.orange,
      },
      red: {
        label: '5+ mm',
        ...radiographicBoneLevelValues.default.bitewings.red,
      },
    },
    periapical: {
      green: {
        label: 'Normal (<15%)',
        ...radiographicBoneLevelValues.default.periapical.green,
      },
      yellow: {
        label: '15-24%',
        ...radiographicBoneLevelValues.default.periapical.yellow,
      },
      orange: {
        label: '24-33%',
        ...radiographicBoneLevelValues.default.periapical.orange,
      },
      red: {
        label: '>33%',
        ...radiographicBoneLevelValues.default.periapical.red,
      },
    },
  },
  moderate: {
    label: 'Option B',
    value: radiographicBoneLevelValues.moderate.value,
    bitewings: {
      green: {
        label: 'Normal (<2.5mm)',
        ...radiographicBoneLevelValues.moderate.bitewings.green,
      },
      yellow: {
        label: '2.5-3.5 mm',
        ...radiographicBoneLevelValues.moderate.bitewings.yellow,
      },
      orange: {
        label: '3.5-4.5 mm',
        ...radiographicBoneLevelValues.moderate.bitewings.orange,
      },
      red: {
        label: '4.5+ mm',
        ...radiographicBoneLevelValues.moderate.bitewings.red,
      },
    },
    periapical: {
      green: {
        label: 'Normal (<13%)',
        ...radiographicBoneLevelValues.moderate.periapical.green,
      },
      yellow: {
        label: '13-22%',
        ...radiographicBoneLevelValues.moderate.periapical.yellow,
      },
      orange: {
        label: '22-31%',
        ...radiographicBoneLevelValues.moderate.periapical.orange,
      },
      red: {
        label: '>31%',
        ...radiographicBoneLevelValues.moderate.periapical.red,
      },
    },
  },
  higher: {
    label: 'Option C',
    value: radiographicBoneLevelValues.higher.value,
    bitewings: {
      green: {
        label: 'Normal (<2mm)',
        ...radiographicBoneLevelValues.higher.bitewings.green,
      },
      yellow: {
        label: '2-3 mm',
        ...radiographicBoneLevelValues.higher.bitewings.yellow,
      },
      orange: {
        label: '3-4 mm',
        ...radiographicBoneLevelValues.higher.bitewings.orange,
      },
      red: {
        label: '4+ mm',
        ...radiographicBoneLevelValues.higher.bitewings.red,
      },
    },
    periapical: {
      green: {
        label: 'Normal (<10%)',
        ...radiographicBoneLevelValues.higher.periapical.green,
      },
      yellow: {
        label: '10-19%',
        ...radiographicBoneLevelValues.higher.periapical.yellow,
      },
      orange: {
        label: '19-28%',
        ...radiographicBoneLevelValues.higher.periapical.orange,
      },
      red: {
        label: '>28%',
        ...radiographicBoneLevelValues.higher.periapical.red,
      },
    },
  },
  maximum: {
    label: 'Option D',
    value: radiographicBoneLevelValues.maximum.value,
    bitewings: {
      green: {
        label: 'Normal (<3mm)',
        ...radiographicBoneLevelValues.maximum.bitewings.green,
      },
      yellow: {
        label: 'N/A',
        ...radiographicBoneLevelValues.maximum.bitewings.yellow,
      },
      orange: {
        label: 'N/A',
        ...radiographicBoneLevelValues.maximum.bitewings.orange,
      },
      red: {
        label: '3+ mm',
        ...radiographicBoneLevelValues.maximum.bitewings.red,
      },
    },
    periapical: {
      green: {
        label: 'Normal (<15%)',
        ...radiographicBoneLevelValues.maximum.periapical.green,
      },
      yellow: {
        label: 'N/A',
        ...radiographicBoneLevelValues.maximum.periapical.yellow,
      },
      orange: {
        label: 'N/A',
        ...radiographicBoneLevelValues.maximum.periapical.orange,
      },
      red: {
        label: '>15%',
        ...radiographicBoneLevelValues.maximum.periapical.red,
      },
    },
  },
} as const;

// Hook for translated labels only
export const useRadiographicBoneLevelLabels = () => {
  const { t } = useLingui();
  return {
    default: {
      label: t`Option A`,
      bitewings: {
        green: { label: t`Normal (<3mm)` },
        yellow: { label: t`3-4 mm` },
        orange: { label: t`4-5 mm` },
        red: { label: t`5+ mm` },
      },
      periapical: {
        green: { label: t`Normal (<15%)` },
        yellow: { label: t`15-24%` },
        orange: { label: t`24-33%` },
        red: { label: t`>33%` },
      },
    },
    moderate: {
      label: t`Option B`,
      bitewings: {
        green: { label: t`Normal (<2.5mm)` },
        yellow: { label: t`2.5-3.5 mm` },
        orange: { label: t`3.5-4.5 mm` },
        red: { label: t`4.5+ mm` },
      },
      periapical: {
        green: { label: t`Normal (<13%)` },
        yellow: { label: t`13-22%` },
        orange: { label: t`22-31%` },
        red: { label: t`>31%` },
      },
    },
    higher: {
      label: t`Option C`,
      bitewings: {
        green: { label: t`Normal (<2mm)` },
        yellow: { label: t`2-3 mm` },
        orange: { label: t`3-4 mm` },
        red: { label: t`4+ mm` },
      },
      periapical: {
        green: { label: t`Normal (<10%)` },
        yellow: { label: t`10-19%` },
        orange: { label: t`19-28%` },
        red: { label: t`>28%` },
      },
    },
    maximum: {
      label: t`Option D`,
      bitewings: {
        green: { label: t`Normal (<3mm)` },
        yellow: { label: t`N/A` },
        orange: { label: t`N/A` },
        red: { label: t`3+ mm` },
      },
      periapical: {
        green: { label: t`Normal (<15%)` },
        yellow: { label: t`N/A` },
        orange: { label: t`N/A` },
        red: { label: t`>15%` },
      },
    },
  } as const;
};

export type RblForToothSide = {
  xCej: number;
  yCej: number;
  xAbd: number;
  yAbd: number;
  xRoot: number;
  yRoot: number;
};

export const mesialDistalQuadrantMap = {
  // This contains a list of mapping of standard distances that helps us to determine the pixel spacing in an image
  1: {
    isLeftMesial: false,
  },
  2: {
    isLeftMesial: true,
  },
  3: {
    isLeftMesial: true,
  },
  4: {
    isLeftMesial: false,
  },
};

export const findRBLAnnotationIndex = (findingPointId: any, rblList = []) => {
  const annotationIndexTuple: any = [];

  for (let i = 0; i < rblList.length; i += 1) {
    // @ts-expect-error TS(2339): Property 'sides' does not exist on type 'never'.
    rblList[i].sides.forEach(
      (rblFindingForTooth: any, rblFindingForToothIndex: any) => {
        rblFindingForTooth.points.forEach((point: any) => {
          if (point.id === findingPointId) {
            annotationIndexTuple.push(i);
            annotationIndexTuple.push(rblFindingForToothIndex);
          }
        });
      },
    );
  }
  return annotationIndexTuple;
};

export const findRBLPointAnnotation = (
  hoveredAnnotationId: any,
  rblList = [],
) => {
  for (let i = 0; i < rblList.length; i += 1) {
    // @ts-expect-error TS(2339): Property 'sides' does not exist on type 'never'.
    const rblFindingForTooth = rblList[i].sides;

    for (let j = 0; j < rblFindingForTooth.length; j += 1) {
      if (rblFindingForTooth[j].points[0]?.id === hoveredAnnotationId) {
        return rblFindingForTooth[j].points[0];
      }
      if (rblFindingForTooth[j].points[1]?.id === hoveredAnnotationId) {
        return rblFindingForTooth[j].points[1];
      }
      if (rblFindingForTooth[j].points[2]?.id === hoveredAnnotationId) {
        return rblFindingForTooth[j].points[2];
      }
    }
  }
  return {};
};

export const computeQuadrant = (toothNumber: any) => Math.ceil(toothNumber / 8);

export const getAbdCejAndRootPoints = (rblFindingsPerSide: any) => {
  let xCej: number;
  let yCej: number;
  let xAbd: number;
  let yAbd: number;
  let xRoot: number;
  let yRoot: number;

  rblFindingsPerSide?.points?.forEach((rblPoint: any) => {
    if (rblPoint.rblType === 'cej') {
      xCej = rblPoint.x;
      yCej = rblPoint.y;
    }
    if (rblPoint.rblType === 'abd') {
      xAbd = rblPoint.x;
      yAbd = rblPoint.y;
    }
    if (rblPoint.rblType === 'root') {
      xRoot = rblPoint.x;
      yRoot = rblPoint.y;
    }
  });
  // @ts-expect-error TS(2454): Variable 'xCej' is used before being assigned.
  return { xCej, yCej, xAbd, yAbd, xRoot, yRoot };
};

/**
 * Determine the vertical distances among the left points, and the right points, for RBL display
 */
export const computeBoneLossMeasurements = (
  findingsForTooth: any,
  isPAImage: any,
) => {
  const [rblLine1, rblLine2] = findingsForTooth;

  let sideIndication1 = null;
  if (rblLine1) {
    sideIndication1 = rblLine1.distanceMM;
  }

  let sideIndication2 = null;
  if (rblLine2) {
    sideIndication2 = rblLine2.distanceMM;
  }

  const mesialMeasurement =
    rblLine1.side === 'mesial' ? sideIndication1 : sideIndication2;
  const distalMeasurement =
    rblLine1.side === 'distal' ? sideIndication1 : sideIndication2;

  const unknown1Measurement = sideIndication1;
  const unknown2Measurement = sideIndication2;

  return {
    mesialMeasurement,
    distalMeasurement,
    unknown1Measurement,
    unknown2Measurement,
  };
};

export const determineColorComparedToSeverityBounds = (
  lineLength: number,
  moderateLowerBound: number,
  moderateUpperBound: number,
  severeLowerBound: number,
): string => {
  if (lineLength > severeLowerBound) {
    return AnnotatorConstants.COLORS.RED;
  }
  if (lineLength > moderateUpperBound && lineLength <= severeLowerBound) {
    return AnnotatorConstants.COLORS.ORANGE;
  }
  if (lineLength >= moderateLowerBound && lineLength <= moderateUpperBound) {
    return AnnotatorConstants.COLORS.YELLOW;
  }
  if (lineLength && lineLength < moderateLowerBound) {
    return AnnotatorConstants.COLORS.GREEN;
  }

  return AnnotatorConstants.COLORS.WHITE;
};

export const determineClassComparedToSeverityBounds = (
  lineLength: number,
  moderateLowerBound: number,
  moderateUpperBound: number,
  severeLowerBound: number,
): string => {
  if (lineLength > severeLowerBound) {
    return 'tooth-rbl-red';
  }
  if (lineLength > moderateUpperBound && lineLength <= severeLowerBound) {
    return 'tooth-rbl-orange';
  }
  if (lineLength >= moderateLowerBound && lineLength <= moderateUpperBound) {
    return 'tooth-rbl-yellow';
  }
  if (lineLength && lineLength < moderateLowerBound) {
    return 'tooth-rbl-green';
  }

  return 'tooth-rbl-white';
};

export const determineRBLLineColor = (
  annotationIsHovered: boolean,
  lineLength: number,
  isPAImage: boolean,
  viewerMode: number,
  practiceRBLThreshold: keyof typeof radiographicBoneLevelSetting = 'default',
): string => {
  if (
    annotationIsHovered &&
    viewerMode !== AnnotatorConstants.MODES.MAGNIFYING
  ) {
    return AnnotatorConstants.COLORS.hover;
  }

  if (isPAImage) {
    const periapicalThreshold =
      radiographicBoneLevelSetting[practiceRBLThreshold].periapical;
    return determineColorComparedToSeverityBounds(
      lineLength,
      periapicalThreshold.yellow.inclusiveLowerBound,
      periapicalThreshold.orange.inclusiveLowerBound,
      periapicalThreshold.red.inclusiveLowerBound,
    );
  }

  const bitewingThreshold =
    radiographicBoneLevelSetting[practiceRBLThreshold].bitewings;
  return determineColorComparedToSeverityBounds(
    lineLength,
    bitewingThreshold.yellow.inclusiveLowerBound,
    bitewingThreshold.orange.inclusiveLowerBound,
    bitewingThreshold.red.inclusiveLowerBound,
  );
};

export const determineRBLLineClass = (
  annotationIsHovered: boolean,
  lineLength: number,
  isPAImage: boolean,
  viewerMode: number,
  practiceRBLThreshold: keyof typeof radiographicBoneLevelSetting = 'default',
): string => {
  if (
    annotationIsHovered &&
    viewerMode !== AnnotatorConstants.MODES.MAGNIFYING
  ) {
    return 'hovered';
  }

  if (isPAImage) {
    const periapicalThreshold =
      radiographicBoneLevelSetting[practiceRBLThreshold].periapical;
    return determineClassComparedToSeverityBounds(
      lineLength,
      periapicalThreshold.yellow.inclusiveLowerBound,
      periapicalThreshold.orange.inclusiveLowerBound,
      periapicalThreshold.red.inclusiveLowerBound,
    );
  }

  const bitewingThreshold =
    radiographicBoneLevelSetting[practiceRBLThreshold].bitewings;
  return determineClassComparedToSeverityBounds(
    lineLength,
    bitewingThreshold.yellow.inclusiveLowerBound,
    bitewingThreshold.orange.inclusiveLowerBound,
    bitewingThreshold.red.inclusiveLowerBound,
  );
};

const useRblMeasurementToDisplay = () => {
  const { t, i18n } = useLingui();
  const rblMeasurementToDisplay = (measurement: any, isPAImage: any) => {
    const numberedMeasurement = Number(measurement);
    if (!numberedMeasurement || Number.isNaN(numberedMeasurement)) {
      return t`Unable to calculate bone level from X-ray`;
    }
    return isPAImage
      ? `${i18n.number(measurement)} %`
      : `${i18n.number(measurement)} mm`;
  };
  return rblMeasurementToDisplay;
};

export const isModerateOrSevereRblFinding = (
  rblLineLength: number,
  isPAImage: boolean,
  practiceRBLThreshold: keyof typeof radiographicBoneLevelSetting = 'default',
): boolean => {
  const periapicalThreshold =
    radiographicBoneLevelSetting[practiceRBLThreshold].periapical;
  const bitewingThreshold =
    radiographicBoneLevelSetting[practiceRBLThreshold].bitewings;
  if (isPAImage) {
    return (
      rblLineLength >=
      Math.min(
        periapicalThreshold.yellow.inclusiveLowerBound,
        periapicalThreshold.red.inclusiveLowerBound,
      )
    );
  }
  return (
    rblLineLength >=
    Math.min(
      bitewingThreshold.yellow.inclusiveLowerBound,
      bitewingThreshold.red.inclusiveLowerBound,
    )
  );
};

export const useGetSideToShowAndMeasurementToDisplay = (input: {
  finding: any;
  mesialMeasurement: any;
  distalMeasurement: any;
  unknown1Measurement: any;
  unknown2Measurement: any;
  isPAImage: boolean;
}) => {
  const { t } = useLingui();
  const rblMeasurementToDisplay = useRblMeasurementToDisplay();
  if (input.finding?.side === 'mesial') {
    return {
      text: t`mesial`,
      measurement: rblMeasurementToDisplay(
        input.mesialMeasurement,
        input.isPAImage,
      ),
    };
  }
  if (input.finding?.side === 'distal') {
    return {
      text: t`distal`,
      measurement: rblMeasurementToDisplay(
        input.distalMeasurement,
        input.isPAImage,
      ),
    };
  }

  if (input.finding?.side === 'Undefined' && input.unknown1Measurement) {
    return {
      text: t`Side 1`,
      measurement: rblMeasurementToDisplay(
        input.unknown1Measurement,
        input.isPAImage,
      ),
    };
  }

  if (input.finding?.side === 'Undefined' && input.unknown2Measurement) {
    return {
      text: t`Side 2`,
      measurement: rblMeasurementToDisplay(
        input.unknown2Measurement,
        input.isPAImage,
      ),
    };
  }

  return {};
};
